import * as Utils from '@js/modules/utils';
import I18n from '@js/modules/translations';

import MapInterface from '@js/modules/maps/mapInterface';
import {ZOOM_SEARCH_LIMIT} from '@js/modules/maps/mapConstants';

// import PWAManager from '@js/modules/pwaManager';


export default class SearchMap extends MapInterface {
    _mapModel = {
        searchOnZoom: false,

        // Force redraw tracks with markers
        redrawAllTracksAndMarkers: false,

        // Common ride ids with the previous request
        loadedRideIds: [],

        // Updated rides to update
        updatedRideIds: [],

        // Show polyline only for this ride
        currentHighlightedRideId: undefined,
        currentTrackRideId: undefined,

        // Behavior for ride tracks
        rideTrackMultiple: false,

        // Filter requests when fetching
        filter: {},

        // Trigger when rides data is changed on zoom or move
        ridesChangedCallback: undefined,

        // Trigger when performance button in a pop-up marker is clicked
        markerPerformanceClicked: []
    };

    constructor(modules, params) {
        super(modules, params);

        if (!this._model.map) {
            return;
        }

        if (params.filter) {
            this._mapModel.filter = params.filter;
        }

        this._mapModel.rideTrackMultiple = params.rideTrackMultiple;

        this._mapModel.searchOnZoom = params.searchOnZoom;

        if (this.hasModule('HELPER') && this.plugHelperEvents) {
            this.plugHelperEvents();
        }

        this.onMapLoaded(() => {
            if (this.hasModule('MARKER_VISIBILITY')) {
                this.plugVisibilityMapEvents({
                    visibleType: 'ride',
                    visibleProperty: 'rideId',
                    skipMapEvents: true
                });
            }

            if (this.hasModule('RIDE_MARKERS')) {
                this.plugRidesMarkers({
                    popupBuilder: this._buildStartMarkerPopup
                });

                this.onRideMarkerClick(this._handleMarkerClick);
                // this.onRideMarkerHover(this._handleMarkerHover);
            }

            if (this.hasModule('RIDE_TRACK')) {
                this.plugRidesTracks();
            }

            if (Utils.isPresent(params.ridesData)) {
                // Ensure data are displayed on map after loading map
                this.showSelected({
                    isMapSearch: false,
                    ridesData: params.ridesData
                });

                // this._updateVisibleMarkers({initData: true});
            } else if (this._mapModel.searchOnZoom) {
                this.fetchSelected();
            }

            if (this._mapModel.searchOnZoom && this.hasModule('HELPER')) {
                this.displayMapHelper(I18n.t('js.maps.helpers.search_on_zoom'));
            }

            this._model.map.on('zoomstart', () => {
                this._model.previousZoom = this._model.map.getZoom();
            });

            this._model.map.on('zoomend', () => {
                if (!this._mapModel.searchOnZoom) {
                    return;
                }

                this._fetchOnZoomDebounce();
            });

            this._model.map.on('dragend', () => {
                if (!this._mapModel.searchOnZoom) {
                    return;
                }

                this._fetchOnZoomDebounce();
            });
        });
    }

    fetchSelected = () => {
        if (!this._model.map) {
            return;
        }

        // if (this._model.map.getZoom() < window.settings.min_zoom_search) {
        // if (this._mapModel.searchOnZoom && this.hasModule('HELPER')) {
        //     this.displayMapHelper(I18n.t('js.maps.helpers.search_on_zoom'));
        // }
        //
        //     return;
        // }

        this._model.mapBounds = this._model.map.getBounds();

        this.displayRideMarkers(
            {
                useBounds: true,
                filter: {
                    ...this._mapModel.filter,
                    limit: this._mapModel.searchOnZoom ? ZOOM_SEARCH_LIMIT : null
                }
            },
            (ridesData) => {
                if (this._visibilityModel?.ridesChangedCallback) {
                    this._visibilityModel.ridesChangedCallback(ridesData);
                }

                this._updateVisibleRides(ridesData);
            });
    };

    showSelected = (params) => {
        if (Utils.isPresent(params.filter)) {
            this._mapModel.filter = params.filter;
        }

        if (typeof params.includeBounds !== 'undefined') {
            this._model.includeBounds = params.includeBounds;
        }

        if (params.clearBounds) {
            this._model.mapBounds = null;
        }

        if (params.minZoom) {
            this._model.minZoom = params.minZoom;
        }

        if (Utils.isPresent(params.ridesData)) {
            this._mapModel.searchOnZoom = false;

            if (this.hasModule('RIDE_MARKERS')) {
                this.displayRideMarkers({
                    clear: true,
                    ridesData: params.ridesData
                });
            }
        } else {
            this._mapModel.searchOnZoom = true;

            this.clear();
        }

        if (!params.isMapSearch) {
            this._fitMapBounds();
        }

        this._updateVisibleRides(params.ridesData);
    };

    toggleSearchOnZoom = (searchOnZoom) => {
        this._mapModel.searchOnZoom = searchOnZoom;
    };

    onRidesChanged = (ridesChangedCallback) => {
        this._visibilityModel.ridesChangedCallback = ridesChangedCallback;
    };

    highlightRide = (rideId, options = {}) => {
        if (!rideId) {
            this.closeAllPopups();

            this._model.hasPopup = false;

            return;
        }

        if (this.hasModule('RIDE_MARKERS')) {
            this.openRideMarkerPopup(rideId, options);
        }
    };

    clear = () => {
        if (this.hasModule('RIDE_MARKERS')) {
            this.clearRidesMarkers();
        }

        if (this.hasModule('RIDE_TRACK')) {
            this.clearRidesTracks();
        }
    };

    /* Private Methods
    ******************** */
    _handleMarkerClick = (rideMarkerProperties = {}) => {
        if (rideMarkerProperties?.id) {
            this._handleStartMarkerInteraction('ride', rideMarkerProperties.id);

            this._mapModel.currentTrackRideId = undefined;
        }
    };

    // _handleMarkerHover = (rideMarkerProperties = {}) => {
    // };

    _fitMapBounds = () => {
        if (!this._model.map) {
            return;
        }

        // get the map bounds by looking at all polylines involved
        if (this._model.mapBounds !== null) {
            return;
        }

        var bounds = this._buildBounds();

        if (this._model.positionMarker) {
            bounds = bounds.extend(this._model.positionMarker.getLngLat());
        }

        if (this.hasModule('RIDE_MARKERS') && this.fitRidesMarkersBounds) {
            this.fitRidesMarkersBounds(bounds);
        }

        if (Utils.isPresent(this._model.includeBounds) && this._model.includeBounds.length === 4) {
            var bbox = [
                [
                    parseFloat(this._model.includeBounds[1]),
                    parseFloat(this._model.includeBounds[0])
                ],
                [
                    parseFloat(this._model.includeBounds[3]),
                    parseFloat(this._model.includeBounds[2])
                ]
            ];
            bounds = bounds.extend(bbox);
        }

        // fit to the overall bounds
        var fitBoundsOptions = {
            // Prevent map zoom on initial map loading
            zoom: this._model.minZoom,
            padding: 120
        };

        this._model.mapBounds = bounds;
        this._model.isAutoZoom = true;
        this.fitBounds(this._model.mapBounds, fitBoundsOptions);
        this._model.isAutoZoom = false;

        // Set minimum zoom
        if (this._model.map.getZoom() > (this._model.minZoom || 16)) {
            this._model.map.setZoom(this._model.minZoom || 16);
        } else {
            // Cannot do this now: map not yet fitted
            // Zoom up to see markers
            // this._model.map.setZoom(this._model.map.getZoom() - 1);
        }
    };

    _fetchOnZoomDebounce = Utils.debounce(() => {
        this.fetchSelected();
    }, 200);

    _buildStartMarkerPopup = (rideFeature) => {
        // var rideId = rideFeature.rideId;
        var rideName = rideFeature.name;
        var rideLinkUrl = rideFeature.linkUrl;
        // var rideLinkName = rideFeature.linkName;
        var rideType = rideFeature.rideType;
        // var rideDistance = rideFeature.distance;
        var rideFormattedDistance = rideFeature.formattedDistance;
        // var rideDuration = rideFeature.duration;
        var rideFormattedDuration = rideFeature.formattedDuration;
        var rideLevel = rideFeature.level;
        var defaultPicture = rideFeature.defaultPicture;

        // var needToUpdate = (this._mapModel.loadedRideIds.indexOf(rideId) === -1 && this._mapModel.updatedRideIds.indexOf(rideId) === -1) || this._mapModel.redrawAllTracksAndMarkers;
        // var correctModeAndType = this._model.type === globalMap.type.SELECTED ||
        //     this._model.mode === globalMap.mode.EXPLORE ||
        //     this._model.mode === globalMap.mode.CHOROPLETH ||
        //     this._model.mode === globalMap.mode.ASSOCIATION;
        // if (!needToUpdate || !correctModeAndType) {
        //     continue;
        // }

        var popupContentElement;
        // Link inside link is not authorized
        if (this._mapModel.markerPerformanceClicked.length) {
            popupContentElement = document.createElement('div');
        } else {
            popupContentElement = document.createElement('a');
        }
        popupContentElement.href = rideLinkUrl;
        // Not working on Safari
        // if (PWAManager.getPWADisplayMode() === PWAManager.MODE.BROWSER) {
        //     popupContentElement.target = '_blank';
        // }
        popupContentElement.className = 'popup-info-box';

        if (!!defaultPicture) {
            var imageContainerElement = document.createElement('div');
            imageContainerElement.className = 'image';
            var imageContentElement = document.createElement('img');
            imageContentElement.src = defaultPicture;
            imageContainerElement.appendChild(imageContentElement);
            popupContentElement.appendChild(imageContainerElement);
        }

        var popupDetailsElement = document.createElement('div');
        popupDetailsElement.className = 'popup-details';

        var titleElement = document.createElement('h3');
        titleElement.className = 'popup-title';
        titleElement.innerHTML = rideName;
        popupDetailsElement.appendChild(titleElement);

        var popupInfoElement = document.createElement('div');
        popupInfoElement.className = 'popup-info';

        var popupInfoMetricsElement = document.createElement('div');
        popupInfoMetricsElement.className = 'popup-info-metrics';
        // popupInfoMetricsElement.innerHTML = '<i class="material-icons">keyboard_tab</i><span>' + rideFormattedDistance + '</span><span class="popup-info-separator"></span><i class="material-icons">schedule</i><span>' + rideFormattedDuration + '</span>';
        popupInfoMetricsElement.innerHTML = '<span>' + rideFormattedDistance + '</span><span class="popup-info-separator"></span><span>' + rideFormattedDuration + '</span>';

        popupInfoElement.appendChild(popupInfoMetricsElement);

        var popupInfoTypeElement = document.createElement('div');
        popupInfoTypeElement.className = 'popup-info-details';
        popupInfoTypeElement.innerHTML = '<span class="popup-info-type">' + I18n.t('js.ride.enums.ride_type')[rideType] + '</span>';
        if (rideLevel !== 'level_undefined') {
            popupInfoTypeElement.innerHTML += '<span class="popup-info-level">' + I18n.t('js.ride.enums.level')[rideLevel] + '</span>';
        }
        popupInfoElement.appendChild(popupInfoTypeElement);

        popupDetailsElement.appendChild(popupInfoElement);

        popupContentElement.appendChild(popupDetailsElement);

        var popupContainerElement = document.createElement('div');
        popupContainerElement.className = rideType + '-popup'; // running-popup / hiking-popup / walk-popup / bike-popup
        popupContainerElement.appendChild(popupContentElement);

        return popupContainerElement.outerHTML;
    };

    _handleStartMarkerInteraction = (type, rideId) => {
        if (!rideId) {
            return;
        }

        if (this.hasModule('RIDE_TRACK')) {
            this.displayRideTrack(
                rideId,
                () => {
                    this._mapModel.currentTrackRideId = rideId;
                },
                {
                    onlyOneTrack: !this._mapModel.rideTrackMultiple,
                    removeIfPresent: true
                }
            );
        }
    };

    _updateVisibleRides = (ridesData) => {
        if (this.hasModule('MARKER_VISIBILITY') && this.updateVisibleMarkers) {
            this.updateVisibleMarkers({visibleRides: ridesData});
        }
    };

    // Called by MapInterface
    _onMapStyleChanged = () => {
        if (this.hasModule('RIDE_MARKERS')) {
            this.resetRidesMarkersStyle();
        }

        if (this.hasModule('RIDE_TRACK')) {
            this.resetRidesTracksStyle();
        }
    };
}
